<template>
  <div class="animated fadeIn user-access-page">

    <!-- HEADER -->
    <div class="pk-page-header">
      <div class="pk-page-header__left">
        <div class="pk-page-header__icon pk-page-header__icon--teal">
          <i class="fas fa-users-cog"></i>
        </div>
        <div>
          <h2 class="pk-page-header__title">{{ $t('kortex.userAccess') }}</h2>
          <p class="pk-page-header__sub" v-if="userAccessList.length > 0">{{ userAccessList.length }} total</p>
        </div>
      </div>
      <div class="pk-page-header__right" style="display: flex; align-items: center; gap: 8px;">
        <b-form-input
          v-if="canAssignUsers || loading"
          type="month"
          v-model="selectedMonth"
          class="pk-month-picker"
          style="width: 150px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); color: #fff;"
        ></b-form-input>
        <button v-if="canAssignUsers || loading" class="pk-btn-primary ml-2" @click="openCreateDialog">
          <i class="fas fa-plus"></i> {{ $t('kortex.grantAccess') }}
        </button>
      </div>
    </div>

    <!-- Upgrade gate -->
    <div v-if="!loading && !canAssignUsers" class="pk-upgrade-gate">
      <div class="pk-upgrade-gate__icon"><i class="fas fa-lock"></i></div>
      <h3 class="pk-upgrade-gate__title">{{ $t('kortex.upgradeToUseUserAccess') }}</h3>
      <p class="pk-upgrade-gate__desc">{{ $t('kortex.upgradeToUseUserAccessDesc') }}</p>
      <router-link :to="{ name: 'marketplace.List', query: { open: 'protokol-kortex' } }" class="pk-upgrade-gate__btn">
        <i class="fas fa-arrow-up"></i> {{ $t('kortex.upgradePlan') }}
      </router-link>
    </div>

    <!-- CARD -->
    <div v-if="loading || canAssignUsers" class="pk-card">
      <pk-data-table
        :columns="columns"
        :rows="userAccessList"
        :loading="loading"
        row-key="uuid"
        empty-icon="fas fa-users-cog"
        :empty-text="$t('kortex.noUserAccess')"
      >
        <template #cell(user_uuid)="{ row }">
          <span class="pk-mono">{{ userNameByUUID(row.user_uuid) }}</span>
        </template>

        <template #cell(credits)="{ row }">
          <span class="pk-credits">
            <span class="pk-credits__used">{{ formatCredits(monthlyUsage[row.uuid]) }}</span>
            <span class="pk-credits__sep">/</span>
            <span class="pk-credits__limit">{{ formatCredits(row.credit_limit) }}</span>
          </span>
          <div class="pk-credits-bar">
            <div class="pk-credits-bar__fill" :style="{ width: creditPercent(row) + '%' }" :class="creditBarClass(row)"></div>
          </div>
        </template>

        <template #cell(agents)="{ row }">
          <span v-if="row.allowed_agent_uuids && row.allowed_agent_uuids.length">
            <span v-for="(agentUUID, idx) in row.allowed_agent_uuids" :key="agentUUID" class="pk-kb-badge">
              {{ agentNameByUUID(agentUUID) }}
              <span v-if="idx < row.allowed_agent_uuids.length - 1">, </span>
            </span>
          </span>
          <span v-else class="pk-date">—</span>
        </template>

        <template #cell(status)="{ row }">
          <span class="pk-status-badge" :class="'pk-status-badge--' + (row.status || 'inactive')">
            {{ row.status === 'active' ? $t('active') : $t('inactive') }}
          </span>
        </template>

        <template #cell(actions)="{ row }">
          <div class="pk-actions-row">
            <button class="pk-btn-sm pk-btn-outline" @click="openTopUpDialog(row)" :title="$t('kortex.topUp')">
              <i class="fas fa-coins"></i>
            </button>
            <button class="pk-btn-sm pk-btn-outline" @click="openEditDialog(row)">
              <i class="fas fa-pencil-alt"></i>
            </button>
            <button class="pk-btn-sm pk-btn-outline" @click="openUsageDialog(row)" :title="$t('kortex.usageBreakdown')">
              <i class="fas fa-chart-bar"></i>
            </button>
            <button class="pk-btn-sm pk-btn-outline pk-btn-outline--danger" @click="confirmDelete(row)">
              <i class="fas fa-trash"></i>
            </button>
          </div>
        </template>
      </pk-data-table>
    </div>

    <!-- Create / Edit dialog -->
    <main-dialog
      :show="showFormDialog"
      :title="editingAccess ? $t('kortex.editUserAccess') : $t('kortex.grantAccess')"
      @close="closeFormDialog"
    >
      <b-form @submit.prevent="save">
        <b-form-group :label="$t('kortex.userUUID')" label-for="ua-user-uuid">
          <v-select
            id="ua-user-uuid"
            v-model="form.user_uuid"
            :options="availableUserOptions"
            :reduce="opt => opt.value"
            label="text"
            :disabled="!!editingAccess || loadingUsers"
            :placeholder="loadingUsers ? $t('loading') : $t('kortex.selectUser')"
            :filterable="true"
            class="pk-user-select"
          >
            <template #option="opt">
              <div class="pk-user-option">
                <span class="pk-user-option__name">{{ opt.name }}</span>
                <span class="pk-user-option__badge" :class="opt.type === 'api' ? 'pk-user-option__badge--api' : ''">
                  {{ opt.type === 'api' ? 'API' : 'Platform' }}
                </span>
                <span v-if="opt.detail" class="pk-user-option__detail">{{ opt.detail }}</span>
              </div>
            </template>
            <template #selected-option="opt">
              <span>{{ opt.text }}</span>
            </template>
          </v-select>
        </b-form-group>
        <b-form-group :label="$t('kortex.creditLimit')" label-for="ua-credit-limit">
          <b-form-input
            id="ua-credit-limit"
            v-model.number="form.credit_limit"
            type="number"
            min="0"
            step="1"
            :placeholder="$t('kortex.creditLimitPlaceholder')"
          ></b-form-input>
          <small v-if="form.credit_limit > 0" class="pk-currency-hint">
            ≈ {{ formatCurrency(form.credit_limit) }}
          </small>
        </b-form-group>
        <b-form-group :label="$t('kortex.allowedAgents')" label-for="ua-agents">
          <div v-if="agents.length === 0" class="kb-empty-hint">
            <i class="fas fa-robot"></i> {{ $t('kortex.noAgents') }}
          </div>
          <div v-else class="kb-pill-grid kb-pill-grid--inline">
            <div
              v-for="agent in agents"
              :key="agent.uuid"
              class="kb-pill"
              :class="{ 'kb-pill--selected': form.allowed_agent_uuids.indexOf(agent.uuid) !== -1 }"
              @click.stop="toggleAgent(agent.uuid)"
            >
              <span class="kb-pill__icon"><i class="fas fa-robot"></i></span>
              <span class="kb-pill__name">{{ agent.name }}</span>
              <span class="kb-pill__check"><i class="fas fa-check"></i></span>
            </div>
          </div>
        </b-form-group>
        <b-form-group v-if="editingAccess" :label="$t('status')" label-for="ua-status">
          <b-form-select id="ua-status" v-model="form.status" :options="statusOptions"></b-form-select>
        </b-form-group>
      </b-form>
      <template #footer>
        <b-button variant="secondary" @click="closeFormDialog">{{ $t('cancel') }}</b-button>
        <b-button variant="primary" :disabled="saving" @click="save">
          <b-spinner v-if="saving" small class="mr-1"></b-spinner>
          {{ $t('save') }}
        </b-button>
      </template>
    </main-dialog>

    <!-- Top-up dialog -->
    <main-dialog
      :show="showTopUpDialog"
      :title="$t('kortex.topUpCredits')"
      @close="showTopUpDialog = false"
    >
      <b-form @submit.prevent="doTopUp">
        <b-form-group :label="$t('kortex.topUpAmount')" label-for="topup-amount">
          <b-form-input
            id="topup-amount"
            v-model.number="topUpForm.amount"
            type="number"
            min="1"
            step="1"
            required
          ></b-form-input>
          <small v-if="topUpForm.amount > 0" class="pk-currency-hint">
            ≈ {{ formatCurrency(topUpForm.amount) }}
          </small>
        </b-form-group>
        <b-form-group :label="$t('kortex.reason')" label-for="topup-reason">
          <b-form-input
            id="topup-reason"
            v-model="topUpForm.reason"
            :placeholder="$t('kortex.reasonPlaceholder')"
          ></b-form-input>
        </b-form-group>
      </b-form>
      <template #footer>
        <b-button variant="secondary" @click="showTopUpDialog = false">{{ $t('cancel') }}</b-button>
        <b-button variant="primary" :disabled="toppingUp" @click="doTopUp">
          <b-spinner v-if="toppingUp" small class="mr-1"></b-spinner>
          {{ $t('kortex.topUp') }}
        </b-button>
      </template>
    </main-dialog>

    <!-- Usage breakdown dialog -->
    <main-dialog
      :show="showUsageDialog"
      :title="$t('kortex.usageBreakdown')"
      @close="showUsageDialog = false"
    >
      <div v-if="loadingBreakdown" class="text-center py-3">
        <b-spinner></b-spinner>
      </div>
      <div v-else-if="usageBreakdown.length === 0" class="text-center py-3" style="color: rgba(255,255,255,0.4);">
        {{ $t('kortex.noUsageData') }}
      </div>
      <table v-else class="pk-usage-table">
        <thead>
          <tr>
            <th>{{ $t('kortex.month') }}</th>
            <th class="pk-usage-table__right">{{ $t('kortex.creditsUsed') }}</th>
            <th class="pk-usage-table__right">{{ $t('kortex.creditLimit') }}</th>
            <th class="pk-usage-table__right">%</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="m in usageBreakdown" :key="m.year + '-' + m.month">
            <td>{{ m.year }}-{{ String(m.month).padStart(2, '0') }}</td>
            <td class="pk-usage-table__right pk-usage-table__used">{{ formatCredits(m.credits) }}</td>
            <td class="pk-usage-table__right pk-usage-table__limit">
              {{ usageDialogUser && usageDialogUser.credit_limit ? formatCredits(usageDialogUser.credit_limit) : '—' }}
            </td>
            <td class="pk-usage-table__right">
              <span v-if="usageDialogUser && usageDialogUser.credit_limit > 0">
                {{ Math.min(100, Math.round((m.credits / usageDialogUser.credit_limit) * 100)) }}%
              </span>
              <span v-else>—</span>
            </td>
          </tr>
        </tbody>
      </table>
      <template #footer>
        <b-button variant="secondary" @click="showUsageDialog = false">{{ $t('close') }}</b-button>
      </template>
    </main-dialog>

    <!-- Delete confirmation dialog -->
    <main-dialog
      :show="showDeleteDialog"
      :title="$t('kortex.revokeAccess')"
      @close="showDeleteDialog = false"
    >
      <p>{{ $t('kortex.confirmRevokeAccess') }}</p>
      <template #footer>
        <b-button variant="secondary" @click="showDeleteDialog = false">{{ $t('cancel') }}</b-button>
        <b-button variant="danger" :disabled="deleting" @click="doDelete">
          <b-spinner v-if="deleting" small class="mr-1"></b-spinner>
          {{ $t('delete') }}
        </b-button>
      </template>
    </main-dialog>

  </div>
</template>

<script>
import VSelect from 'vue-select'

export default {
  name: 'UserAccess',
  components: { VSelect },
  data: function () {
    var now = new Date()
    var mm = (now.getMonth() + 1).toString().padStart(2, '0')
    var defaultMonth = now.getFullYear() + '-' + mm
    return {
      loading: false,
      loadingUsers: false,
      saving: false,
      toppingUp: false,
      deleting: false,
      showFormDialog: false,
      showTopUpDialog: false,
      showDeleteDialog: false,
      showUsageDialog: false,
      usageDialogUser: null,
      usageBreakdown: [],
      loadingBreakdown: false,
      editingAccess: null,
      pendingDelete: null,
      pendingTopUp: null,
      projectUsers: [],
      apiUsers: [],
      selectedMonth: defaultMonth,
      monthlyUsage: {},
      form: {
        user_uuid: '',
        credit_limit: 0,
        allowed_agent_uuids: []
      },
      topUpForm: {
        amount: 10,
        reason: ''
      },
      billingCurrency: 'USD',
      exchangeRate: 1
    }
  },
  computed: {
    canAssignUsers: function () {
      return this.$store.getters['cortex/canAssignUsers']
    },
    userAccessList: function () {
      return this.$store.state.cortex.userAccessList
    },
    agents: function () {
      return this.$store.state.cortex.agents
    },
    statusOptions: function () {
      return [
        { value: 'active', text: this.$t('active') },
        { value: 'inactive', text: this.$t('inactive') }
      ]
    },
    availableUserOptions: function () {
      var self = this
      var grantedUUIDs = {}
      self.userAccessList.forEach(function (ua) {
        grantedUUIDs[ua.user_uuid] = true
      })
      var platformOptions = self.projectUsers
        .filter(function (u) { return !grantedUUIDs[u.user_uuid] })
        .map(function (u) {
          var name = (u.user && u.user.name) || u.user_uuid
          var email = (u.user && u.user.email) || ''
          var label = email ? name + ' (' + email + ')' : name
          return { value: u.user_uuid, text: label, name: name, detail: email, type: 'platform' }
        })
      var apiOptions = self.apiUsers
        .filter(function (u) { return !grantedUUIDs[u.uuid] })
        .map(function (u) {
          return { value: u.uuid, text: u.username + ' (API User)', name: u.username, detail: '', type: 'api' }
        })
      return platformOptions.concat(apiOptions)
    },
    columns: function () {
      return [
        { key: 'user_uuid', label: this.$t('kortex.userUUID') },
        { key: 'credits', label: this.$t('credits') },
        { key: 'agents', label: this.$t('kortex.allowedAgents') },
        { key: 'status', label: this.$t('status') },
        { key: 'actions', label: '', tdClass: 'pk-td-actions' }
      ]
    }
  },
  mounted: function () {
    this.load()
  },
  watch: {
    selectedMonth: function () {
      this.loadMonthlyUsage()
    }
  },
  methods: {
    load: function () {
      var self = this
      self.loading = true

      // Fetch project billing currency and exchange rate
      self.$transactionApi.get('/billing/balance').then(function (resp) {
        if (resp && resp.data) {
          if (resp.data.currency) {
            self.billingCurrency = resp.data.currency
          }
          if (resp.data.balance_usd != null && resp.data.balance_display != null && resp.data.balance_usd > 0) {
            self.exchangeRate = resp.data.balance_display / resp.data.balance_usd
          }
        }
      }).catch(function () {})

      Promise.all([
        self.$store.dispatch('cortex/loadTier'),
        self.$store.dispatch('cortex/loadUserAccessList'),
        self.$store.dispatch('cortex/loadAgents'),
        self.loadProjectUsers()
      ]).then(function () {
        self.loadMonthlyUsage()
      }).finally(function () {
        self.loading = false
      })
    },
    loadMonthlyUsage: function () {
      var self = this
      var list = self.userAccessList
      if (!list || list.length === 0) return
      list.forEach(function (ua) {
        self.$http.get('/luma/kortex/v1/user-access/' + ua.uuid + '/usage', {
          params: { month: self.selectedMonth }
        }).then(function (resp) {
          var used = (resp.data && resp.data.credits_used) || 0
          self.$set(self.monthlyUsage, ua.uuid, used)
        }).catch(function () {
          self.$set(self.monthlyUsage, ua.uuid, 0)
        })
      })
    },
    loadProjectUsers: function () {
      var self = this
      self.loadingUsers = true
      return Promise.all([
        self.$project.users().then(function (resp) {
          self.projectUsers = resp.data || []
        }).catch(function () {
          self.projectUsers = []
        }),
        self.$project.apiUsers().then(function (resp) {
          self.apiUsers = resp.data || []
        }).catch(function () {
          self.apiUsers = []
        })
      ]).finally(function () {
        self.loadingUsers = false
      })
    },
    openCreateDialog: function () {
      this.editingAccess = null
      this.form = { user_uuid: '', credit_limit: 0, allowed_agent_uuids: [] }
      this.showFormDialog = true
    },
    openEditDialog: function (ua) {
      this.editingAccess = ua
      this.form = {
        credit_limit: ua.credit_limit || 0,
        allowed_agent_uuids: (ua.allowed_agent_uuids || []).slice(),
        status: ua.status || 'active'
      }
      this.showFormDialog = true
    },
    closeFormDialog: function () {
      this.showFormDialog = false
      this.editingAccess = null
    },
    save: function () {
      var self = this
      if (!self.form.user_uuid && !self.editingAccess) return
      self.saving = true
      var action = self.editingAccess
        ? self.$store.dispatch('cortex/updateUserAccess', Object.assign({ uuid: self.editingAccess.uuid }, self.form))
        : self.$store.dispatch('cortex/createUserAccess', self.form)
      action.then(function () {
        self.closeFormDialog()
        if (!self.editingAccess) {
          self.$store.dispatch('cortex/loadUserAccessList').then(function () {
            self.loadMonthlyUsage()
          })
        }
      }).finally(function () {
        self.saving = false
      })
    },
    openTopUpDialog: function (ua) {
      this.pendingTopUp = ua
      this.topUpForm = { amount: 10, reason: '' }
      this.showTopUpDialog = true
    },
    doTopUp: function () {
      var self = this
      if (!self.pendingTopUp || self.topUpForm.amount <= 0) return
      self.toppingUp = true
      self.$store.dispatch('cortex/topUpCredits', {
        uuid: self.pendingTopUp.uuid,
        amount: self.topUpForm.amount,
        reason: self.topUpForm.reason
      }).then(function () {
        self.showTopUpDialog = false
        self.pendingTopUp = null
        return self.$store.dispatch('cortex/loadUserAccessList').then(function () {
          self.loadMonthlyUsage()
        })
      }).finally(function () {
        self.toppingUp = false
      })
    },
    confirmDelete: function (ua) {
      this.pendingDelete = ua
      this.showDeleteDialog = true
    },
    doDelete: function () {
      var self = this
      if (!self.pendingDelete) return
      self.deleting = true
      self.$store.dispatch('cortex/deleteUserAccess', self.pendingDelete.uuid).then(function () {
        self.showDeleteDialog = false
        self.pendingDelete = null
      }).finally(function () {
        self.deleting = false
      })
    },
    toggleAgent: function (uuid) {
      var idx = this.form.allowed_agent_uuids.indexOf(uuid)
      if (idx === -1) {
        this.form.allowed_agent_uuids.push(uuid)
      } else {
        this.form.allowed_agent_uuids.splice(idx, 1)
      }
    },
    userNameByUUID: function (uuid) {
      var user = this.projectUsers.find(function (u) { return u.user_uuid === uuid })
      if (user && user.user) {
        var name = user.user.name || ''
        var email = user.user.email || ''
        return email ? name + ' (' + email + ')' : name
      }
      var apiUser = this.apiUsers.find(function (u) { return u.uuid === uuid })
      if (apiUser) {
        return apiUser.username + ' (API User)'
      }
      return uuid
    },
    agentNameByUUID: function (uuid) {
      var agent = this.agents.find(function (a) { return a.uuid === uuid })
      return agent ? agent.name : uuid
    },
    formatCredits: function (val) {
      if (val === null || val === undefined) return '0'
      var n = Number(val)
      return n.toLocaleString() + ' (' + this.formatCurrency(n) + ')'
    },
    formatCurrency: function (credits) {
      var n = Number(credits) || 0
      var converted = this.exchangeRate !== 1 ? n * this.exchangeRate : n
      if (this.billingCurrency === 'USD') {
        return '$' + converted.toFixed(2)
      }
      return converted.toFixed(2) + ' ' + this.billingCurrency
    },
    creditPercent: function (row) {
      var limit = row.credit_limit || 0
      if (limit <= 0) return 0
      return Math.min(100, ((this.monthlyUsage[row.uuid] || 0) / limit) * 100)
    },
    creditBarClass: function (row) {
      var pct = this.creditPercent(row)
      if (pct >= 90) return 'pk-credits-bar__fill--danger'
      if (pct >= 70) return 'pk-credits-bar__fill--warning'
      return ''
    },
    openUsageDialog: function (row) {
      var self = this
      self.usageDialogUser = row
      self.usageBreakdown = []
      self.loadingBreakdown = true
      self.showUsageDialog = true
      self.$http.get('/luma/kortex/v1/user-access/' + row.uuid + '/usage/breakdown').then(function (resp) {
        self.usageBreakdown = (resp.data && resp.data.breakdown) || []
      }).catch(function () {
        self.usageBreakdown = []
      }).finally(function () {
        self.loadingBreakdown = false
      })
    }
  }
}
</script>

<style scoped>
.user-access-page {
  padding: 8px 0;
}
.pk-mono {
  font-family: 'SF Mono', 'Menlo', monospace;
  font-size: 12px;
  color: rgba(255,255,255,0.6);
}
.pk-credits {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 13px;
  font-weight: 500;
}
.pk-credits__used { color: #f97316; }
.pk-credits__sep { color: rgba(255,255,255,0.3); }
.pk-credits__limit { color: rgba(255,255,255,0.7); }
.pk-credits-bar {
  width: 100%;
  height: 4px;
  background: rgba(255,255,255,0.06);
  border-radius: 2px;
  margin-top: 4px;
  overflow: hidden;
}
.pk-credits-bar__fill {
  height: 100%;
  background: #22c55e;
  border-radius: 2px;
  transition: width 0.3s;
}
.pk-credits-bar__fill--warning { background: #f59e0b; }
.pk-credits-bar__fill--danger { background: #ef4444; }
.pk-status-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  text-transform: uppercase;
}
.pk-status-badge--active {
  background: rgba(34,197,94,0.15);
  color: #22c55e;
}
.pk-status-badge--inactive {
  background: rgba(255,255,255,0.06);
  color: rgba(255,255,255,0.4);
}
.pk-btn-sm {
  font-size: 12px;
  padding: 4px 10px;
}
.pk-btn-outline {
  background: transparent;
  border: 1px solid rgba(255,255,255,0.12);
  color: rgba(255,255,255,0.7);
  border-radius: 6px;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
}
.pk-btn-outline:hover { border-color: rgba(255,255,255,0.3); color: #fff; }
.pk-btn-outline--danger:hover { border-color: #ef4444; color: #ef4444; }

.pk-actions-row {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 6px;
}

/* Usage breakdown table */
.pk-usage-table {
  width: 100%;
  border-collapse: collapse;
}
.pk-usage-table th,
.pk-usage-table td {
  padding: 8px 10px;
  font-size: 13px;
  border-bottom: 1px solid rgba(255,255,255,0.05);
}
.pk-usage-table th {
  color: rgba(255,255,255,0.4);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.pk-usage-table__right { text-align: right; }
.pk-usage-table__used { color: #f97316; }
.pk-usage-table__limit { color: rgba(255,255,255,0.5); }
.pk-currency-hint {
  display: block;
  margin-top: 4px;
  color: rgba(255,255,255,0.45);
  font-size: 11px;
  letter-spacing: 0.02em;
}

/* Knowledge base pill selector — copied from Agents.vue */
.kb-pill-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}
.kb-pill {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 7px 14px 7px 10px;
  border-radius: 8px;
  border: 1px solid rgba(255,255,255,0.1);
  background: rgba(255,255,255,0.03);
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  user-select: none;
}
.kb-pill:hover {
  border-color: rgba(108,128,255,0.4);
  background: rgba(108,128,255,0.08);
}
.kb-pill--selected {
  border-color: rgba(108,128,255,0.7);
  background: rgba(108,128,255,0.14);
  box-shadow: 0 0 0 1px rgba(108,128,255,0.25);
}
.kb-pill__icon {
  color: rgba(255,255,255,0.45);
  font-size: 13px;
  display: flex;
}
.kb-pill__name {
  font-size: 13px;
  font-weight: 500;
}
.kb-pill__check {
  opacity: 0;
  transition: opacity 0.15s;
  color: #6c80ff;
  font-size: 12px;
  display: flex;
}
.kb-pill--selected .kb-pill__check {
  opacity: 1;
}
.kb-empty-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  border-radius: 8px;
  background: rgba(255,255,255,0.02);
  color: rgba(255,255,255,0.35);
  font-size: 13px;
}

/* v-select dark theme overrides */
.pk-user-select >>> .vs__dropdown-toggle {
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 6px;
  padding: 4px 8px;
  min-height: 38px;
}
.pk-user-select >>> .vs__dropdown-toggle:focus-within {
  border-color: rgba(108,128,255,0.5);
}
.pk-user-select >>> .vs__selected {
  color: #fff;
}
.pk-user-select >>> .vs__search {
  color: #fff;
}
.pk-user-select >>> .vs__search::placeholder {
  color: rgba(255,255,255,0.35);
}
.pk-user-select >>> .vs__actions svg {
  fill: rgba(255,255,255,0.4);
}
.pk-user-select >>> .vs__dropdown-menu {
  background: #1e1e2e;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 6px;
  margin-top: 2px;
}
.pk-user-select >>> .vs__dropdown-option {
  color: rgba(255,255,255,0.8);
  padding: 8px 12px;
}
.pk-user-select >>> .vs__dropdown-option--highlight {
  background: rgba(108,128,255,0.15);
  color: #fff;
}

/* Custom option layout */
.pk-user-option {
  display: flex;
  align-items: center;
  gap: 8px;
}
.pk-user-option__name {
  font-weight: 500;
}
.pk-user-option__badge {
  font-size: 10px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 3px;
  text-transform: uppercase;
  background: rgba(255,255,255,0.08);
  color: rgba(255,255,255,0.5);
}
.pk-user-option__badge--api {
  background: rgba(249,115,22,0.15);
  color: #f97316;
}
.pk-user-option__detail {
  color: rgba(255,255,255,0.4);
  font-size: 12px;
}
</style>
